import { useEffect, useRef } from 'react';

import { IInfiniteScrollConfig } from '../DataTable.types';

interface UseInfiniteScrollProps {
  infiniteScrollConfig?: IInfiniteScrollConfig;
  isLoading: boolean;
}

interface UseInfiniteScrollReturn {
  loadMoreRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook that manages infinite scroll functionality using IntersectionObserver.
 *
 * Sets up an IntersectionObserver that watches a sentinel element and triggers
 * the `onLoadMore` callback when it becomes visible in the viewport.
 *
 * @param props - The hook configuration.
 * @param props.infiniteScrollConfig - Configuration for infinite scroll behavior.
 * @param props.isLoading - Whether the table is in initial loading state.
 * @returns Object containing the ref to attach to the sentinel element.
 *
 * @example
 * ```tsx
 * const { loadMoreRef } = useInfiniteScroll({
 *   infiniteScrollConfig: {
 *     enabled: true,
 *     hasMore: hasNextPage,
 *     onLoadMore: fetchNextPage,
 *     isLoadingMore: isFetchingNextPage
 *   },
 *   isLoading: false
 * });
 *
 * return <div ref={loadMoreRef} />;
 * ```
 */
export const useInfiniteScroll = ({
  infiniteScrollConfig,
  isLoading
}: UseInfiniteScrollProps): UseInfiniteScrollReturn => {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const enabled = infiniteScrollConfig?.enabled;
    const hasMore = infiniteScrollConfig?.hasMore;
    const isLoadingMore = infiniteScrollConfig?.isLoadingMore;
    const onLoadMore = infiniteScrollConfig?.onLoadMore;
    const scrollContainer = infiniteScrollConfig?.scrollContainerRef?.current;

    const shouldSkipObserver =
      !enabled || !hasMore || isLoadingMore || isLoading || !onLoadMore;

    if (shouldSkipObserver) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: scrollContainer || null,
        rootMargin: '200px',
        threshold: 0
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    infiniteScrollConfig?.enabled,
    infiniteScrollConfig?.hasMore,
    infiniteScrollConfig?.isLoadingMore,
    infiniteScrollConfig?.onLoadMore,
    infiniteScrollConfig?.scrollContainerRef,
    isLoading
  ]);

  return { loadMoreRef };
};
